/**
 * Subscription Plan Form Modal
 *
 * Dual-purpose form modal for creating or editing a subscription plan.
 * - Create: POST /api/payroll-platform/org-plan/create-subscription-plan
 * - Edit:   POST /api/payroll-platform/org-plan/update-subscription-plan
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save } from 'lucide-react';

import { Modal, Button, Input, Select, Toggle } from '@/components/common';
import { subscriptionService, ApiError } from '@/api/services';
import type {
  SubscriptionPlan,
  CreateSubscriptionPlanRequest,
  UpdateSubscriptionPlanRequest,
} from '@/api/types';
import { useToast } from '@/hooks/useToast';

// ============================================
// Options
// ============================================

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

// ============================================
// Form Schema
// ============================================

const numberField = z
  .string()
  .min(1, 'Required')
  .refine((val) => !isNaN(Number(val)) && Number(val) >= 0, 'Must be a valid number');

const planSchema = z.object({
  subscriptionPlanCode: z.string().min(1, 'Plan code is required'),
  subscriptionPlanName: z.string().min(1, 'Plan name is required'),
  description: z.string().min(1, 'Description is required'),
  status: z.string().min(1, 'Status is required'),

  maxUsers: numberField,
  maxContracts: numberField,
  maxEmployees: numberField,
  totalSmsCredits: numberField,
  maxPersonalVaultSize: numberField,

  customReportTemplatesEnabled: z.boolean(),
  eInvoiceEnabled: z.boolean(),
  smsFeatureEnabled: z.boolean(),
  mobileAppFeatureEnabled: z.boolean(),
  isCustomPlan: z.boolean(),
  liveTrackingEnabled: z.boolean(),
  geoFencingEnabled: z.boolean(),
  liveTrackingPhotosEnabled: z.boolean(),
  biometricAttendanceEnabled: z.boolean(),
  selfieCheckInEnabled: z.boolean(),
  personalVaultEnabled: z.boolean(),
  workLogsEnabled: z.boolean(),
  ticketsEnabled: z.boolean(),
});

type PlanFormData = z.infer<typeof planSchema>;

// Toggle fields rendered in the "Features" section
const FEATURE_TOGGLES: {
  key: keyof PlanFormData;
  label: string;
  description: string;
}[] = [
  { key: 'customReportTemplatesEnabled', label: 'Custom Report Templates', description: 'Allow custom report templates' },
  { key: 'eInvoiceEnabled', label: 'E-Invoice', description: 'Enable e-invoicing' },
  { key: 'smsFeatureEnabled', label: 'SMS Feature', description: 'Enable SMS notifications' },
  { key: 'mobileAppFeatureEnabled', label: 'Mobile App', description: 'Enable mobile app access' },
  { key: 'liveTrackingEnabled', label: 'Live Tracking', description: 'Enable live location tracking' },
  { key: 'geoFencingEnabled', label: 'Geo Fencing', description: 'Enable geo-fencing' },
  { key: 'liveTrackingPhotosEnabled', label: 'Live Tracking Photos', description: 'Capture photos during live tracking' },
  { key: 'biometricAttendanceEnabled', label: 'Biometric Attendance', description: 'Enable biometric attendance' },
  { key: 'selfieCheckInEnabled', label: 'Selfie Check-In', description: 'Enable selfie-based check-in' },
  { key: 'personalVaultEnabled', label: 'Personal Vault', description: 'Enable personal document vault' },
  { key: 'workLogsEnabled', label: 'Work Logs', description: 'Enable work logs' },
  { key: 'ticketsEnabled', label: 'Tickets', description: 'Enable support tickets' },
];

// ============================================
// Defaults / mapping
// ============================================

const EMPTY_VALUES: PlanFormData = {
  subscriptionPlanCode: '',
  subscriptionPlanName: '',
  description: '',
  status: 'active',
  maxUsers: '',
  maxContracts: '',
  maxEmployees: '',
  totalSmsCredits: '',
  maxPersonalVaultSize: '',
  customReportTemplatesEnabled: false,
  eInvoiceEnabled: false,
  smsFeatureEnabled: false,
  mobileAppFeatureEnabled: false,
  isCustomPlan: false,
  liveTrackingEnabled: false,
  geoFencingEnabled: false,
  liveTrackingPhotosEnabled: false,
  biometricAttendanceEnabled: false,
  selfieCheckInEnabled: false,
  personalVaultEnabled: false,
  workLogsEnabled: false,
  ticketsEnabled: false,
};

const numToStr = (n: number | undefined | null): string =>
  n === undefined || n === null ? '' : String(n);

/** Map an existing plan (table row) into form values for editing. */
function planToFormValues(plan: SubscriptionPlan): PlanFormData {
  return {
    subscriptionPlanCode: plan.code ?? '',
    subscriptionPlanName: plan.name ?? '',
    description: plan.description ?? '',
    status: (plan.status ?? 'active').toLowerCase(),
    maxUsers: numToStr(plan.maxUsers),
    maxContracts: numToStr(plan.maxContracts),
    maxEmployees: numToStr(plan.maxEmployees),
    totalSmsCredits: numToStr(plan.totalSmsCredits),
    maxPersonalVaultSize: numToStr(plan.maxPersonalVaultSize),
    customReportTemplatesEnabled: Boolean(plan.customReportTemplatesEnabled),
    eInvoiceEnabled: Boolean(plan.eInvoiceEnabled),
    smsFeatureEnabled: Boolean(plan.smsFeatureEnabled),
    mobileAppFeatureEnabled: Boolean(plan.mobileAppFeatureEnabled),
    isCustomPlan: Boolean(plan.isCustomPlan),
    liveTrackingEnabled: Boolean(plan.liveTrackingEnabled),
    geoFencingEnabled: Boolean(plan.geoFencingEnabled),
    liveTrackingPhotosEnabled: Boolean(plan.liveTrackingPhotosEnabled),
    biometricAttendanceEnabled: Boolean(plan.biometricAttendanceEnabled),
    selfieCheckInEnabled: Boolean(plan.selfieCheckInEnabled),
    personalVaultEnabled: Boolean(plan.personalVaultEnabled),
    workLogsEnabled: Boolean(plan.workLogsEnabled),
    ticketsEnabled: Boolean(plan.ticketsEnabled),
  };
}

// ============================================
// Component Props
// ============================================

interface SubscriptionPlanFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  /** When provided, the modal edits this plan instead of creating a new one. */
  plan?: SubscriptionPlan | null;
}

// ============================================
// Component
// ============================================

export function SubscriptionPlanFormModal({
  open,
  onOpenChange,
  onSuccess,
  plan,
}: SubscriptionPlanFormModalProps) {
  const toast = useToast();
  const isEditMode = Boolean(plan?.id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<PlanFormData>({
    resolver: zodResolver(planSchema),
    defaultValues: EMPTY_VALUES,
  });

  const selectedStatus = watch('status');

  // Prefill (edit) or clear (create) whenever the modal is opened.
  useEffect(() => {
    if (!open) return;
    setFormError(null);
    reset(plan ? planToFormValues(plan) : EMPTY_VALUES);
  }, [open, plan, reset]);

  const handleClose = () => {
    if (!isSubmitting) {
      reset(EMPTY_VALUES);
      setFormError(null);
      onOpenChange(false);
    }
  };

  const onSubmit = async (data: PlanFormData) => {
    setFormError(null);
    setIsSubmitting(true);
    try {
      const planPayload = {
        code: data.subscriptionPlanCode,
        name: data.subscriptionPlanName,
        description: data.description,
        status: data.status.toUpperCase(),
        maxUsers: Number(data.maxUsers),
        maxContracts: Number(data.maxContracts),
        maxEmployees: Number(data.maxEmployees),
        totalSmsCredits: Number(data.totalSmsCredits),
        maxPersonalVaultSize: Number(data.maxPersonalVaultSize),
        customReportTemplatesEnabled: data.customReportTemplatesEnabled,
        eInvoiceEnabled: data.eInvoiceEnabled,
        smsFeatureEnabled: data.smsFeatureEnabled,
        mobileAppFeatureEnabled: data.mobileAppFeatureEnabled,
        isCustomPlan: data.isCustomPlan,
        liveTrackingEnabled: data.liveTrackingEnabled,
        geoFencingEnabled: data.geoFencingEnabled,
        liveTrackingPhotosEnabled: data.liveTrackingPhotosEnabled,
        biometricAttendanceEnabled: data.biometricAttendanceEnabled,
        selfieCheckInEnabled: data.selfieCheckInEnabled,
        personalVaultEnabled: data.personalVaultEnabled,
        workLogsEnabled: data.workLogsEnabled,
        ticketsEnabled: data.ticketsEnabled,
      };

      if (isEditMode && plan) {
        const request: UpdateSubscriptionPlanRequest = {
          subscriptionPlan: { id: plan.id, ...planPayload },
        };
        await subscriptionService.updateSubscriptionPlan(request);
        toast.success('Subscription plan updated successfully');
      } else {
        const request: CreateSubscriptionPlanRequest = {
          subscriptionPlan: planPayload,
        };
        await subscriptionService.createSubscriptionPlan(request);
        toast.success('Subscription plan created successfully');
      }

      reset(EMPTY_VALUES);
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      if (error instanceof ApiError) {
        setFormError(error.showMessage);
      } else {
        setFormError(
          `Failed to ${isEditMode ? 'update' : 'create'} subscription plan. Please try again.`
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      title={isEditMode ? 'Edit Subscription Plan' : 'Create Subscription Plan'}
      description={
        isEditMode
          ? 'Update the plan limits and features'
          : 'Define a new subscription plan with its limits and features'
      }
      size="full"
      closeOnOverlayClick={!isSubmitting}
      footer={
        <>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            loading={isSubmitting}
            leftIcon={<Save className="h-4 w-4" />}
            onClick={handleSubmit(onSubmit)}
          >
            {isEditMode ? 'Update Plan' : 'Create Plan'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {formError && (
          <div className="rounded-lg bg-danger/10 border border-danger/20 px-4 py-3">
            <p className="text-sm text-danger dark:text-danger-dark">{formError}</p>
          </div>
        )}

        {/* Section: Plan Details */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground dark:text-foreground-dark border-b border-border dark:border-border-dark pb-2">
            Plan Details
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Plan Code"
              placeholder="e.g. PREMIUM"
              required
              disabled={isEditMode}
              error={errors.subscriptionPlanCode?.message}
              {...register('subscriptionPlanCode')}
            />
            <Input
              label="Plan Name"
              placeholder="e.g. Premium Plan"
              required
              error={errors.subscriptionPlanName?.message}
              {...register('subscriptionPlanName')}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Description"
              placeholder="Enter plan description"
              required
              error={errors.description?.message}
              {...register('description')}
            />
            <Select
              label="Status"
              placeholder="Select status"
              required
              options={STATUS_OPTIONS}
              value={selectedStatus}
              onChange={(value) => setValue('status', value)}
              errorMessage={errors.status?.message}
            />
          </div>
          <Toggle
            label="Custom Plan"
            description="Mark this as a custom plan instead of a standard plan"
            checked={Boolean(watch('isCustomPlan'))}
            onChange={(checked) =>
              setValue('isCustomPlan', checked, { shouldValidate: false })
            }
          />
        </div>

        {/* Section: Limits */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground dark:text-foreground-dark border-b border-border dark:border-border-dark pb-2">
            Limits
          </h3>
          <div className="grid gap-4 md:grid-cols-3">
            <Input
              label="Max Users"
              type="number"
              placeholder="e.g. 100"
              required
              error={errors.maxUsers?.message}
              {...register('maxUsers')}
            />
            <Input
              label="Max Contracts"
              type="number"
              placeholder="e.g. 50"
              required
              error={errors.maxContracts?.message}
              {...register('maxContracts')}
            />
            <Input
              label="Max Employees"
              type="number"
              placeholder="e.g. 500"
              required
              error={errors.maxEmployees?.message}
              {...register('maxEmployees')}
            />
            <Input
              label="Total SMS Credits"
              type="number"
              placeholder="e.g. 10000"
              required
              error={errors.totalSmsCredits?.message}
              {...register('totalSmsCredits')}
            />
            <Input
              label="Max Personal Vault Size (MB)"
              type="number"
              placeholder="e.g. 5120"
              required
              error={errors.maxPersonalVaultSize?.message}
              {...register('maxPersonalVaultSize')}
            />
          </div>
        </div>

        {/* Section: Features */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground dark:text-foreground-dark border-b border-border dark:border-border-dark pb-2">
            Features
          </h3>
          <div className="grid gap-x-8 gap-y-3 rounded-lg border border-border dark:border-border-dark p-5 bg-secondary-50 dark:bg-secondary-900/30 md:grid-cols-2">
            {FEATURE_TOGGLES.map((feature) => (
              <Toggle
                key={feature.key}
                label={feature.label}
                description={feature.description}
                checked={Boolean(watch(feature.key))}
                onChange={(checked) =>
                  setValue(feature.key, checked as never, { shouldValidate: false })
                }
              />
            ))}
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default SubscriptionPlanFormModal;
